/**
 * Event Types for AI Participant Event Dispatcher
 *
 * This file defines all event types that the AI system can dispatch and handle.
 * Events are triggered by simulation changes (phase transitions, conversation ends, etc.)
 * and handled by appropriate AI services (ConversationManager, ReflectionService, etc.).
 */

/**
 * Base event interface - all events extend this
 */
export interface BaseEvent {
  type: EventType
  timestamp: Date
  runId: string
  /**
   * Flag to indicate if this event originated in the current tab
   * - true: Event was dispatched from THIS tab (perform ALL actions including queuing)
   * - false/undefined: Event was received via BroadcastChannel from ANOTHER tab (UI updates only, NO queuing)
   *
   * This prevents duplicate queue entries when multiple tabs are open
   */
  _isOriginatingTab?: boolean
}

/**
 * Event types enum - defines all possible event types
 */
export enum EventType {
  PHASE_CHANGE = 'PHASE_CHANGE',
  CONVERSATION_END = 'CONVERSATION_END',
  CONVERSATION_STARTED = 'CONVERSATION_STARTED',
  CONVERSATION_MESSAGE = 'CONVERSATION_MESSAGE',
  VOICE_CONVERSATION_STARTED = 'VOICE_CONVERSATION_STARTED',
  PUBLIC_SPEECH_STARTED = 'PUBLIC_SPEECH_STARTED',
  VOTE_SESSION_START = 'VOTE_SESSION_START',
  VOTE_RESULTS_ANNOUNCED = 'VOTE_RESULTS_ANNOUNCED',
  KING_DECISION_ANNOUNCED = 'KING_DECISION_ANNOUNCED',
  MANUAL_REFLECTION_TRIGGER = 'MANUAL_REFLECTION_TRIGGER'
}

/**
 * Phase Change Event
 * Triggered when simulation phase changes (e.g., entering "Free Consultations" phase)
 *
 * Handlers should:
 * - Check if phase requires automatic AI actions
 * - Start clan councils if phase is consultation phase
 * - Update AI context with phase information
 */
export interface PhaseChangeEvent extends BaseEvent {
  type: EventType.PHASE_CHANGE
  phaseId: string
  phaseName: string
  previousPhaseId: string | null
  previousPhaseName: string | null
}

/**
 * Conversation End Event
 * Triggered when any conversation/speech completes
 *
 * Handlers should:
 * - Determine conversation type
 * - Identify participants needing reflection
 * - Trigger reflection with rich context (transcript, phase, time remaining)
 * - For public speeches: deliver to ALL AI participants in run
 */
export interface ConversationEndEvent extends BaseEvent {
  type: EventType.CONVERSATION_END
  meetingId: string
  participants: string[] // Array of role_ids
  transcript: ConversationMessage[]
  conversationType:
    | 'clan_council'           // AI-AI text conversation
    | 'free_consultation'      // AI-AI text conversation
    | 'strategic_meeting'      // AI-AI text conversation
    | 'voice_conversation'     // Human-AI voice conversation
    | 'public_speech'          // AI public speech (broadcast to all)
}

/**
 * Conversation message structure
 */
export interface ConversationMessage {
  speakerId: string // role_id
  speakerName: string
  message: string
  timestamp: Date
}

/**
 * Conversation Started Event
 * Triggered when a new AI-AI conversation begins
 */
export interface ConversationStartedEvent extends BaseEvent {
  type: EventType.CONVERSATION_STARTED
  meetingId: string
  participants: string[] // Array of role_ids
  conversationType: 'clan_council' | 'free_consultation'
}

/**
 * Conversation Message Event
 * Triggered when a new message is generated in a conversation
 */
export interface ConversationMessageEvent extends BaseEvent {
  type: EventType.CONVERSATION_MESSAGE
  meetingId: string
  message: ConversationMessage
  turnNumber: number
}

/**
 * Voice Conversation Started Event
 * Triggered when a human-AI voice conversation begins
 *
 * This event signals the AI External Interface to switch to voice conversation mode
 */
export interface VoiceConversationStartedEvent extends BaseEvent {
  type: EventType.VOICE_CONVERSATION_STARTED
  meetingId: string
  roleId: string // AI participant role ID
  roleName: string
  humanParticipant: {
    name: string
    position?: string | null
    context?: string
  }
  intentNotes: {
    goal: string
    tone: string
    tactics?: string[]
    boundaries?: string[]
    time_limit?: string
  }
  fullPrompt: string
  voiceAgentId: string
}

/**
 * Public Speech Started Event
 * Triggered when an AI participant begins a public speech
 *
 * This event signals the AI External Interface to switch to public speech mode
 */
export interface PublicSpeechStartedEvent extends BaseEvent {
  type: EventType.PUBLIC_SPEECH_STARTED
  meetingId: string
  roleId: string // AI participant role ID
  roleName: string
  speechTopic: string
  speechContext?: string
  intentNotes: {
    goal: string
    tone: string
    tactics?: string[]
    boundaries?: string[]
    time_limit?: string
  }
  fullPrompt: string
  voiceAgentId: string
}

/**
 * Vote Session Start Event
 * Triggered when a vote session begins (for future voting implementation)
 *
 * Handlers should:
 * - Notify relevant AI participants
 * - Trigger voting decision process
 */
export interface VoteSessionStartEvent extends BaseEvent {
  type: EventType.VOTE_SESSION_START
  sessionId: string
  voteType: string
  targetClanId?: string | null
}

/**
 * Vote Results Announced Event
 * Triggered when vote results are revealed (clan nominations, elections, allegiances, etc.)
 *
 * Handlers should:
 * - Trigger reflection for ALL AI participants
 * - Update AI context with vote outcomes
 * - Include rich context: winner, vote counts, session details
 */
export interface VoteResultsAnnouncedEvent extends BaseEvent {
  type: EventType.VOTE_RESULTS_ANNOUNCED
  sessionId: string
  voteType: string // 'clan_nomination', 'election_round', 'clan_allegiance', etc.
  voteFormat: string // 'choose_person', 'yes_no'
  title: string
  description: string | null
  winnerRoleId: string | null // Winner if choose_person format
  winnerRoleName: string | null
  totalVotes: number
  participantCount: number
  voteBreakdown?: { // Optional vote counts per candidate
    roleId: string
    roleName: string
    voteCount: number
    percentage: number
  }[]
  clanId: string | null // For clan-specific votes
  resultsData?: Record<string, any> // Full results_data JSONB from vote_results table
}

/**
 * King Decision Announced Event
 * Triggered when King's decisions are revealed
 *
 * Handlers should:
 * - Trigger reflection for all AI participants
 * - Update AI context with King's appointments
 */
export interface KingDecisionAnnouncedEvent extends BaseEvent {
  type: EventType.KING_DECISION_ANNOUNCED
  decisionId: string
  economicAdvisorRoleId: string
  seniorJudgeRoleId: string
}

/**
 * Manual Reflection Trigger Event
 * Triggered by facilitator via UI (manual override)
 *
 * Handlers should:
 * - Start reflection for specific AI participant
 * - Update status to 'updating'
 */
export interface ManualReflectionTriggerEvent extends BaseEvent {
  type: EventType.MANUAL_REFLECTION_TRIGGER
  roleId: string
  reason: string
}

/**
 * Union type of all possible events
 */
export type AIEvent =
  | PhaseChangeEvent
  | ConversationEndEvent
  | ConversationStartedEvent
  | ConversationMessageEvent
  | VoiceConversationStartedEvent
  | PublicSpeechStartedEvent
  | VoteSessionStartEvent
  | VoteResultsAnnouncedEvent
  | KingDecisionAnnouncedEvent
  | ManualReflectionTriggerEvent

/**
 * Event handler function type
 * Handlers are async functions that process events
 */
export type EventHandler<T extends AIEvent = AIEvent> = (event: T) => Promise<void>

/**
 * Event handler registry type
 * Maps event types to arrays of handlers
 */
export type EventHandlerRegistry = {
  [K in EventType]?: EventHandler[]
}
